import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { rhm, apiError } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, PageHead } from "../ui";

export default function RhmRoles() {
    const { can, user: me } = useRhmAuth();
    const qc = useQueryClient();
    const [selected, setSelected] = useState("co_admin");
    const [draft, setDraft] = useState({});

    const { data: roles = [] } = useQuery({
        queryKey: ["rhm-roles"],
        queryFn: async () => (await rhm.get("/roles")).data,
    });
    const { data: registry } = useQuery({
        queryKey: ["rhm-permission-registry"],
        queryFn: async () => (await rhm.get("/auth/permission-registry")).data,
    });

    const role = roles.find((r) => r.key === selected);
    useEffect(() => { if (role) setDraft(role.permissions || {}); }, [role?.key, roles.length]); // eslint-disable-line

    const save = useMutation({
        mutationFn: async () => (await rhm.patch(`/roles/${selected}`, {
            name: role.name, description: role.description || "", permissions: draft,
        })).data,
        onSuccess: () => { toast.success("Permissions updated"); qc.invalidateQueries({ queryKey: ["rhm-roles"] }); },
        onError: (e) => toast.error(apiError(e)),
    });

    const toggle = (module, action) => {
        const current = draft[module] || [];
        setDraft({
            ...draft,
            [module]: current.includes(action) ? current.filter((a) => a !== action) : [...current, action],
        });
    };

    const modules = registry?.modules || {};
    const locked = selected === "super_admin";
    const editable = can("roles.edit") && !locked;

    return (
        <div data-testid="rhm-roles-page">
            <PageHead title="Roles & Permissions"
                sub="Permissions are dynamic. Toggle exactly what each role may view, create, edit, delete, export, approve or manage."
                action={editable && <Btn onClick={() => save.mutate()} disabled={save.isPending} data-testid="rhm-save-permissions">
                    {save.isPending ? "SAVING…" : "SAVE PERMISSIONS"}
                </Btn>} />

            <div className="flex flex-wrap gap-2 mb-5">
                {roles.map((r) => (
                    <button key={r.key} onClick={() => setSelected(r.key)} data-testid={`rhm-role-tab-${r.key}`}
                        className={`mono text-[10px] tracking-[0.16em] px-4 py-3 border ${
                            selected === r.key ? "border-gold text-gold bg-gold/5" : "border-white/12 text-zinc-400 hover:text-white"}`}>
                        {r.name.toUpperCase()} · {r.user_count}
                    </button>
                ))}
            </div>

            {locked && (
                <div className="mb-5 border border-gold/30 bg-gold/5 px-4 py-3 text-sm text-zinc-300">
                    The Super Admin role always has unrestricted access and cannot be reduced.
                    Revoking user access, deleting users and deleting roles are permanently Super Admin only actions.
                </div>
            )}
            {selected === "co_admin" && (
                <div className="mb-5 border border-white/12 px-4 py-3 text-sm text-zinc-400">
                    Co-Admin has full operational access but is blocked at API level from revoking user access,
                    deleting users and modifying Super Admin records — even if toggled on here.
                </div>
            )}

            <Card title={role ? `${role.name} permissions` : "Permissions"}>
                {!role ? <Empty msg="Select a role." /> : (
                    <div className="overflow-x-auto -mx-5 px-5">
                        <table className="w-full min-w-[760px] text-sm">
                            <thead>
                                <tr className="border-b border-white/10 text-left">
                                    <th className="mono text-[10px] tracking-[0.16em] text-zinc-500 font-normal py-3">MODULE</th>
                                    {["view", "create", "edit", "delete", "export", "approve", "manage"].map((a) => (
                                        <th key={a} className="mono text-[10px] tracking-[0.16em] text-zinc-500 font-normal py-3 px-3 text-center">
                                            {a.toUpperCase()}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {Object.entries(modules).map(([key, mod]) => (
                                    <tr key={key} data-testid={`rhm-perm-row-${key}`}>
                                        <td className="py-3 pr-4">
                                            <div className="font-semibold">{mod.label}</div>
                                            <div className="mono text-[9px] text-zinc-600">{key}</div>
                                        </td>
                                        {["view", "create", "edit", "delete", "export", "approve", "manage"].map((action) => {
                                            const supported = mod.actions.includes(action);
                                            const on = locked ? true : (draft[key] || []).includes(action);
                                            return (
                                                <td key={action} className="py-3 px-3 text-center">
                                                    {supported ? (
                                                        <button
                                                            type="button"
                                                            disabled={!editable}
                                                            aria-pressed={on}
                                                            onClick={() => toggle(key, action)}
                                                            data-testid={`rhm-perm-${key}-${action}`}
                                                            className={`w-9 h-5 relative transition-colors ${on ? "bg-gold" : "bg-white/10"} ${editable ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
                                                        >
                                                            <span className={`absolute top-0.5 w-4 h-4 bg-black transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
                                                        </button>
                                                    ) : <span className="text-zinc-700">—</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Card title="Hard-coded security guarantees" className="mt-5">
                <ul className="text-sm text-zinc-400 space-y-2 list-disc pl-5">
                    <li>Only the Super Admin can revoke or restore another user's access.</li>
                    <li>Only the Super Admin can archive a user account or delete a custom role.</li>
                    <li>Super Admin records cannot be edited, re-roled or deactivated by anyone else.</li>
                    <li>At least one active Super Admin must always remain.</li>
                    <li>Every permission change is written to the Audit Log.</li>
                </ul>
                {!me?.is_super_admin && <div className="mt-4"><Badge tone="warn">YOU ARE NOT A SUPER ADMIN</Badge></div>}
            </Card>
        </div>
    );
}
